import { vec2, vec3 } from 'gl-matrix';

/**
 * An authored polygon-prism volumetric cloud region. The XZ polygon (`polygon`)
 * is extruded between `baseY` and `topY`; `CloudVolumePass` raymarches 3D FBM
 * noise inside the AABB-clipped ray segment per fragment.
 *
 * Engine-side mirror of the persisted `CloudVolumeRef` manifest entry. Data is
 * intentionally simple — the polygon stays as-is; the AABB is recomputed
 * lazily so editor edits to baseY/topY/polygon flow through without rebuild
 * orchestration.
 */
export class CloudVolume {
  public id: string;
  public name: string;
  public polygon: number[][];
  public baseY: number;
  public topY: number;
  public edgeFeather: number;
  public density: number;
  public coverage: number;
  public noiseScale: number;
  public windDir: vec2;
  public windSpeed: number;

  // Cached XZ AABB derived from `polygon`. Set on construction and whenever
  // the polygon is replaced via {@link setPolygon}; baseY/topY contribute to
  // the world-space AABB returned from {@link getWorldAabb}.
  private _aabbMinXZ: vec2 = vec2.fromValues(0, 0);
  private _aabbMaxXZ: vec2 = vec2.fromValues(0, 0);

  constructor(init: {
    id: string;
    name: string;
    polygon: number[][];
    baseY: number;
    topY: number;
    edgeFeather?: number;
    density?: number;
    coverage?: number;
    noiseScale?: number;
    windDirX?: number;
    windDirZ?: number;
    windSpeed?: number;
  }) {
    this.id = init.id;
    this.name = init.name;
    this.polygon = init.polygon;
    this.baseY = init.baseY;
    this.topY = init.topY;
    this.edgeFeather = init.edgeFeather ?? 5;
    this.density = init.density ?? 0.5;
    this.coverage = init.coverage ?? 0.5;
    this.noiseScale = init.noiseScale ?? 0.02;
    this.windDir = vec2.fromValues(init.windDirX ?? 1, init.windDirZ ?? 0);
    this.windSpeed = init.windSpeed ?? 1.5;
    this.recomputeAabbXZ();
  }

  setPolygon(polygon: number[][]): void {
    this.polygon = polygon;
    this.recomputeAabbXZ();
  }

  /** XZ-only AABB, useful for tile binning and quick rejection. */
  getAabbMinXZ(): vec2 {
    return this._aabbMinXZ;
  }

  getAabbMaxXZ(): vec2 {
    return this._aabbMaxXZ;
  }

  /**
   * World-space AABB combining the XZ polygon bounds with `[baseY, topY]`.
   * Returned via out params to avoid alloc on the per-frame call site.
   */
  getWorldAabb(outMin: vec3, outMax: vec3): void {
    outMin[0] = this._aabbMinXZ[0];
    outMin[1] = this.baseY;
    outMin[2] = this._aabbMinXZ[1];
    outMax[0] = this._aabbMaxXZ[0];
    outMax[1] = this.topY;
    outMax[2] = this._aabbMaxXZ[1];
  }

  private recomputeAabbXZ(): void {
    if (this.polygon.length === 0) {
      vec2.set(this._aabbMinXZ, 0, 0);
      vec2.set(this._aabbMaxXZ, 0, 0);
      return;
    }
    let minX = this.polygon[0][0];
    let maxX = this.polygon[0][0];
    let minZ = this.polygon[0][1];
    let maxZ = this.polygon[0][1];
    for (let i = 1; i < this.polygon.length; i++) {
      const x = this.polygon[i][0];
      const z = this.polygon[i][1];
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (z < minZ) minZ = z;
      if (z > maxZ) maxZ = z;
    }
    vec2.set(this._aabbMinXZ, minX, minZ);
    vec2.set(this._aabbMaxXZ, maxX, maxZ);
  }
}
